<template>
  <div ref="rootEl" class="relative min-w-0">
    <div class="relative">
      <Search class="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
      <input ref="inputEl" v-model="query" type="text" role="combobox" autocomplete="off" maxlength="120"
        aria-autocomplete="list" aria-controls="page-finder-results" :aria-expanded="isOpen && (!!query.trim() || !!recent.length)"
        :aria-activedescendant="highlighted >= 0 ? 'admin-search-hit-' + highlighted : undefined"
        placeholder="صفحه، دانش‌آموز، سؤال…" aria-label="جست‌وجو در صفحه‌ها و داده‌های پنل"
        class="input-field page-finder-field ps-9 pe-10" data-testid="page-finder-input"
        @focus="isOpen = true" @keydown.down.prevent="move(1)" @keydown.up.prevent="move(-1)"
        @keydown.enter="onEnter" @keydown.esc.stop.prevent="dismiss" @compositionstart="composing = true" @compositionend="composing = false" />
      <button v-if="query" type="button" class="absolute end-1 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="پاک‌کردن جست‌وجو" @click="clear"><X :size="14" /></button>
      <span v-else class="pointer-events-none absolute end-2 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded border border-gray-200 text-xs text-gray-400 dark:border-gray-700">/</span>
    </div>
    <div v-if="isOpen && (query.trim() || recent.length)" id="page-finder-results" role="listbox" aria-label="نتایج جست‌وجوی پنل"
      class="fixed inset-x-4 top-16 z-10 max-h-[min(70vh,480px)] overflow-y-auto rounded-xl border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-900 lg:absolute lg:inset-x-auto lg:start-0 lg:top-full lg:mt-2 lg:w-[480px] lg:min-w-full" data-testid="page-finder-results">
      <template v-if="!query.trim()">
        <div class="flex items-center justify-between px-3 py-2 text-xs text-gray-500"><span>جست‌وجوهای اخیر</span><button type="button" class="hover:text-red-600" @click="clearRecent">پاک‌کردن تاریخچه</button></div>
        <div v-for="(term, index) in recent" :key="term" class="flex items-center rounded-lg" :class="highlighted === index ? 'bg-indigo-50 dark:bg-indigo-950/50' : 'hover:bg-gray-50 dark:hover:bg-gray-800'">
          <button :id="'admin-search-hit-' + index" type="button" role="option" :aria-selected="highlighted === index" class="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-start text-sm" @mouseenter="highlighted = index" @click="recall(term)"><History :size="14" class="shrink-0 text-gray-400" /><span class="truncate" :title="term">{{ term }}</span></button>
          <button type="button" class="me-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:text-red-600" :aria-label="'حذف جست‌وجوی ' + term" @click="removeRecent(term)"><X :size="14" /></button>
        </div>
      </template>
      <template v-for="group in suggestionGroups" :key="group.type">
        <div class="flex items-center justify-between px-3 pb-1 pt-2 text-xs text-gray-500" role="presentation"><span>{{ group.label }}</span><span>{{ fa(group.items.length) }}</span></div>
        <button v-for="hit in group.items" :id="'admin-search-hit-' + hit.index" :key="hit.key" type="button" role="option"
          :aria-selected="highlighted === hit.index" :data-testid="hit.kind === 'page' ? 'page-finder-hit' : 'global-search-hit'"
          class="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-start" :class="highlighted === hit.index ? 'bg-indigo-50 dark:bg-indigo-950/50' : 'hover:bg-gray-50 dark:hover:bg-gray-800'"
          @mouseenter="highlighted = hit.index" @mousedown.prevent @click="go(hit)">
          <component :is="iconFor(hit.icon)" class="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
          <span class="min-w-0 flex-1"><span class="block truncate text-sm font-medium text-gray-800 dark:text-gray-100" :title="hit.title">{{ hit.title }}</span><span v-if="hit.subtitle" class="block truncate text-xs text-gray-500 dark:text-gray-400" :dir="hit.kind === 'page' ? undefined : 'auto'" :title="hit.subtitle">{{ hit.subtitle }}</span></span>
          <ChevronLeft class="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
        </button>
      </template>
      <div v-if="loading" role="status" class="flex items-center gap-2 px-3 py-3 text-xs text-gray-500"><Loader2 :size="14" class="animate-spin" />جست‌وجو در داده‌ها…</div>
      <div v-else-if="dataError" role="status" class="flex items-center justify-between gap-2 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"><span>{{ dataError }}</span><button type="button" class="shrink-0 rounded-lg px-2 py-1 hover:bg-amber-50 dark:hover:bg-amber-950" @click="retry">تلاش دوباره</button></div>
      <div v-else-if="query.trim() && !allHits.length" class="px-3 py-3 text-xs text-gray-500">
        {{ query.trim().length < 2 && canSearchData() ? 'برای جست‌وجوی داده‌ها دست‌کم دو نویسه بنویسید.' : 'نتیجه‌ای پیدا نشد؛ بخشی از نام یا شناسه را امتحان کنید.' }}
        <RouterLink v-if="!canSearchData() && can('questions.read_key')" to="/question-bank/questions" class="mt-1 block text-primary hover:underline" @click="close">بانک سؤال و فیلتر نشانهٔ بازبینی</RouterLink>
      </div>
      <button v-if="canSearchData() && query.trim().length >= 2" type="button" class="mt-1 flex w-full items-center justify-between rounded-lg border-t border-gray-100 px-3 py-2 text-sm font-medium text-primary hover:bg-indigo-50 dark:border-gray-800 dark:hover:bg-indigo-950/50" data-testid="global-search-all" @mousedown.prevent @click="openAll"><span>دیدن همهٔ نتایج</span><span class="text-xs font-normal text-gray-400">Enter</span></button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Search, X, History, LayoutDashboard, Users, FileQuestion, BookOpen, CreditCard, FlaskConical, ChevronLeft, Loader2 } from 'lucide-vue-next'
import { readRecentSearches, saveRecentSearch, writeRecentSearches } from '@/utils/recentAdminSearch'
import { usePermission } from '@/composables/usePermission'
import { adminPageIndex, findAdminPages, permittedSearchGroups } from '@/utils/adminSearch'
import api from '@/services/api'

const router = useRouter()
const route = useRoute()
const { can } = usePermission()
const rootEl = ref(null), inputEl = ref(null)
const query = ref(''), isOpen = ref(false), highlighted = ref(-1), composing = ref(false)
const dataGroups = ref([]), loading = ref(false), dataError = ref('')
const recent = ref(readRecentSearches())
const refreshRecent = () => { recent.value = readRecentSearches() }
const clearRecent = () => { recent.value = writeRecentSearches([]); highlighted.value = -1 }
const removeRecent = term => { recent.value = writeRecentSearches(recent.value.filter(item => item !== term)); highlighted.value = -1 }
const recall = term => { query.value = term; isOpen.value = true; inputEl.value?.focus() }
let debounceTimer, controller, requestId = 0
const icons = { Search, LayoutDashboard, Users, FileQuestion, BookOpen, CreditCard, FlaskConical }
const iconFor = name => icons[name] || Search
const fa = value => Number(value).toLocaleString('fa-IR')
const canSearchData = () => can('learners.read')
const pageIndex = () => adminPageIndex(can)
const results = computed(() => findAdminPages(query.value, can))
const suggestionGroups = computed(() => {
  let index = 0
  const groups = [{ type: 'pages', label: 'صفحه‌های پنل', items: results.value.map(hit => ({ ...hit, key: 'page:' + hit.path, link: hit.path, kind: 'page', icon: 'LayoutDashboard', subtitle: hit.section })) },
    ...dataGroups.value.map(group => ({ ...group, items: group.items.slice(0, 3).map(hit => ({ ...hit, key: group.type + ':' + hit.id, kind: 'data', icon: group.icon })) }))]
  return groups.filter(group => group.items.length).map(group => ({ ...group, items: group.items.map(hit => ({ ...hit, index: index++ })) }))
})
const allHits = computed(() => suggestionGroups.value.flatMap(group => group.items))
async function searchData(term, id) {
  if (!canSearchData() || term.length < 2 || id !== requestId) return
  controller?.abort()
  controller = new AbortController()
  loading.value = true
  dataError.value = ''
  try {
    const { data } = await api.get('/search', { params: { q: term }, signal: controller.signal })
    if (id === requestId && term === query.value.trim()) dataGroups.value = permittedSearchGroups(data?.data?.groups || [], can)
  } catch (error) {
    if (id === requestId && error.code !== 'ERR_CANCELED') dataError.value = 'جست‌وجوی داده‌ها انجام نشد.'
  } finally {
    if (id === requestId) loading.value = false
  }
}
const retry = () => searchData(query.value.trim(), ++requestId)
watch(query, value => {
  clearTimeout(debounceTimer)
  controller?.abort()
  const id = ++requestId
  highlighted.value = -1
  dataGroups.value = []
  dataError.value = ''
  loading.value = false
  const term = value.trim()
  if (canSearchData() && term.length >= 2) {
    loading.value = true
    debounceTimer = setTimeout(() => searchData(term, id), 300)
  }
})
watch(() => route.fullPath, () => close())
const move = async step => {
  const count = query.value.trim() ? allHits.value.length : recent.value.length
  if (!count) return
  isOpen.value = true
  highlighted.value = highlighted.value < 0 ? (step > 0 ? 0 : count - 1) : (highlighted.value + step + count) % count
  await nextTick()
  rootEl.value?.querySelector('#admin-search-hit-' + highlighted.value)?.scrollIntoView?.({ block: 'nearest' })
}
const close = () => { isOpen.value = false; query.value = ''; highlighted.value = -1 }
const dismiss = () => { isOpen.value = false; highlighted.value = -1; inputEl.value?.blur() }
const clear = () => { query.value = ''; inputEl.value?.focus() }
const go = hit => { if (hit) { saveRecentSearch(query.value); router.push(hit.link || hit.path); close() } }
const openAll = () => {
  if (!canSearchData() || query.value.trim().length < 2) return
  saveRecentSearch(query.value)
  router.push({ path: '/search', query: { q: query.value.trim() } })
  close()
}
const onEnter = event => {
  if (event.isComposing || composing.value) return
  event.preventDefault()
  if (!query.value.trim()) { if (highlighted.value >= 0 && recent.value[highlighted.value]) recall(recent.value[highlighted.value]); return }
  if (highlighted.value >= 0) go(allHits.value[highlighted.value])
  else if (results.value.length === 1 && !dataGroups.value.some(group => group.items.length)) go(allHits.value[0])
  else if (canSearchData()) openAll()
  else go(allHits.value[0])
}
const onDocumentClick = event => { if (rootEl.value && !rootEl.value.contains(event.target)) isOpen.value = false }
const onFocusSearch = event => {
  if (document.querySelector('#main-content input[type="search"]')) return
  event.preventDefault()
  isOpen.value = true
  inputEl.value?.focus()
}
onMounted(() => { window.addEventListener('admin:recent-searches', refreshRecent); window.addEventListener('admin:focus-search', onFocusSearch); document.addEventListener('click', onDocumentClick) })
onUnmounted(() => { clearTimeout(debounceTimer); ++requestId; controller?.abort(); window.removeEventListener('admin:recent-searches', refreshRecent); window.removeEventListener('admin:focus-search', onFocusSearch); document.removeEventListener('click', onDocumentClick) })
defineExpose({ results, pageIndex })
</script>

<style scoped>
.page-finder-field { width: 100%; height: 36px; min-height: 36px; @apply border-gray-200 bg-gray-50 text-sm transition-colors placeholder:text-gray-400 hover:border-gray-300 focus:border-brand-400 focus:bg-white dark:border-gray-700 dark:bg-gray-800/60 dark:focus:bg-gray-900; }
</style>
